/**
 * Kickard PHASE 2.2.4 — Online session persistence (browser/tab-close survival).
 *
 * Stores the active online match identity in `localStorage` (NOT
 * `sessionStorage`) so it survives a deliberate tab/browser close and
 * the player can be auto-reattached when they reopen the site within
 * the TTL window.
 *
 * Schema:
 *   { roomCode, playerId, token, name, opponentName, savedAt(ms) }
 *
 * Cleared explicitly on:
 *   • Leave button
 *   • Match end (summary screen)
 *   • Reconnect budget exhausted
 *   • Server-issued non-recoverable error (4404, 4401)
 *
 * Why localStorage with a SHORT TTL (not session/cookie):
 *   • Survives browser-close (the user's reported flow).
 *   • Short TTL prevents ghost matches if the user truly walks away.
 *   • Tab-scoped sessionStorage was the wrong tool for this requirement.
 */
const KEY = 'kickard.online.session.v1';
// 10-minute TTL — long enough that the creator can share the room code
// with a friend (Viber/WhatsApp), put the phone in their pocket, and
// come back to a still-joinable game. Matches the server's
// WAITING_ROOM_TTL_MINUTES.
const TTL_MS = 10 * 60_000;

export function saveOnlineSession(session) {
  if (!session || !session.roomCode || !session.token || !session.playerId) return;
  try {
    const payload = { ...session, savedAt: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(payload));
    // eslint-disable-next-line no-console
    console.log('[online-session] saved', {
      roomCode: session.roomCode,
      playerId: session.playerId,
      tokenPrefix: String(session.token).slice(0, 6),
      ttlMs: TTL_MS,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[online-session] save failed', e);
  }
}

export function loadOnlineSession() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.roomCode || !obj.token || !obj.playerId) return null;
    if (typeof obj.savedAt === 'number' && (Date.now() - obj.savedAt) > TTL_MS) {
      localStorage.removeItem(KEY);
      // eslint-disable-next-line no-console
      console.log('[online-session] expired (ttl)');
      return null;
    }
    return obj;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[online-session] load failed', e);
    return null;
  }
}

export function loadOnlineSessionForRoom(roomCode) {
  const s = loadOnlineSession();
  if (!s) return null;
  if (String(s.roomCode).toUpperCase() !== String(roomCode || '').toUpperCase()) return null;
  return s;
}

export function clearOnlineSession() {
  try {
    localStorage.removeItem(KEY);
    // eslint-disable-next-line no-console
    console.log('[online-session] cleared');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[online-session] clear failed', e);
  }
}
